"use client";

import { c as _c } from "react/compiler-runtime";
import { Completion, CompletionHeader } from "../components/completion";
import { ShopUpsellPrompt } from "../components/shop-cta";
import { useQuery } from "@tanstack/react-query";
import { ReactNode, useEffect, useRef } from "react";
import { IconType } from "react-icons";
import { FaCalendarAlt, FaCheck, FaMapMarkerAlt, FaUserNurse } from "react-icons/fa";
import { HiArrowRight, HiMiniDocumentText } from "react-icons/hi2";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { PatientPortalSignUpPrompt } from "@/components/patient/patient-portal-signup-prompt";
import { Button } from "@/components/ui";
import { useSelfScheduleContext } from "@/contexts/self-schedule-provider";
import { usePatientClaim } from "@/hooks/patient-claim/use-patient-claim";
import { usePatientVerification } from "@/hooks/self-schedule/use-patient-verification";
import { isWithinAppointmentManagementGuard } from "@/lib/appointment-management";
import { recordConversion } from "@/lib/conversion-events";
import { createFormLink } from "@/lib/create-form-link";
import { formatAddress, formatDateWithDay, formatList, formatTime } from "@/lib/format-data";
import { trackBookingComplete } from "@/lib/gtm-events";
import { VIRTUAL_VISIT_DETAIL, formatVisitTime } from "@/lib/self-schedule-virtual-services";
import { flattenSelectedServices } from "@/lib/service-addon-utils";
import { computeTotalBookingFeeCents } from "@/lib/service-fees";
import { cx } from "@/lib/tailwind-utils";
import { useStoreTimezone } from "@/lib/timezone";
import { selfScheduleQueryOptions } from "@/api/check-in/self-schedule/self-schedule-query";
import { tenantQueryOptions } from "@/query/tenant";
import { useSelfScheduleStore } from "@/stores/self-schedule-store";
interface BookingCompleteProps {
  className?: string;
}
const SummaryRow = t0 => {
  const $ = _c(15);
  const {
    icon: Icon,
    eyebrow,
    title,
    detail
  } = t0;
  let t1;
  if ($[0] !== Icon) {
    t1 = <div className="mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-foreground/5 text-foreground-muted"><Icon className="h-4 w-4" /></div>;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== eyebrow) {
    t2 = <p className="text-caption font-medium uppercase tracking-wide text-foreground-secondary">{eyebrow}</p>;
    $[2] = eyebrow;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== title) {
    t3 = <h4 className="text-body font-semibold leading-tight text-foreground">{title}</h4>;
    $[4] = title;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== detail) {
    t4 = detail && <div className="text-caption text-foreground-muted">{detail}</div>;
    $[6] = detail;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = <div className="min-w-0 flex-1 space-y-0.5">{t2}{t3}{t4}</div>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t1 || $[13] !== t5) {
    t6 = <div className="flex items-start gap-3 py-4">{t1}{t5}</div>;
    $[12] = t1;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
};
const AppointmentForms = () => {
  const $ = _c(21);
  const pathname = usePathname();
  const tenantSubdomain = pathname.split("/")[1];
  const {
    createdAppointmentId
  } = useSelfScheduleContext();
  let t0;
  if ($[0] !== createdAppointmentId) {
    t0 = selfScheduleQueryOptions.appointmentForms(createdAppointmentId || "");
    $[0] = createdAppointmentId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = !!createdAppointmentId;
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = {
      ...t0,
      enabled: t1
    };
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: appointmentForms,
    error: formsError
  } = useQuery(t2);
  if (!createdAppointmentId) {
    return null;
  }
  if (formsError) {
    let t3;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="motion-preset-slide-right-sm mt-4 rounded-2xl border border-status-error-text/15 bg-status-error-bg/40 p-5"><div className="mb-2 flex items-center gap-2"><HiMiniDocumentText className="h-5 w-5 text-status-error-text" /><h3 className="text-body font-semibold text-foreground">We couldn't load your forms</h3></div><p className="text-body text-foreground-secondary">Your forms will be sent to you via text message. Please complete them before your appointment.</p></div>;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  if (!appointmentForms || appointmentForms.length === 0) {
    return null;
  }
  const formCount = appointmentForms.length;
  let t3;
  if ($[6] !== appointmentForms || $[7] !== formCount) {
    t3 = formCount <= 3 ? appointmentForms.map(_temp).join(", ") : `${appointmentForms.slice(0, 2).map(_temp2).join(", ")} and ${formCount - 2} more`;
    $[6] = appointmentForms;
    $[7] = formCount;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const formNames = t3;
  const t4 = "motion-preset-slide-right-sm mt-4";
  const t5 = "overflow-hidden rounded-xl border border-border bg-background";
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <span className="inline-flex items-center gap-2">Required forms<span className="inline-flex items-center rounded-full bg-status-warning-bg px-1.5 py-0.5 text-tiny font-medium uppercase tracking-wide text-status-warning-text">Action needed</span></span>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = formCount === 1 ? "1 form to complete" : `${formCount} forms to complete`;
  let t8;
  if ($[10] !== formNames) {
    t8 = <span className="line-clamp-2">{formNames}</span>;
    $[10] = formNames;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t7 || $[13] !== t8) {
    t9 = <div className="px-4"><SummaryRow icon={HiMiniDocumentText} eyebrow={t6} title={t7} detail={t8} /></div>;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = createdAppointmentId && <div className="border-t border-border p-3"><Link key={createdAppointmentId} href={createFormLink(tenantSubdomain, createdAppointmentId)} target="_blank" rel="noopener noreferrer" className="block"><Button variant="primary" className="group w-full"><span>Complete forms now</span><HiArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" /></Button></Link></div>;
  let t11;
  if ($[15] !== t10 || $[16] !== t9) {
    t11 = <div className={t5}>{t9}{t10}</div>;
    $[15] = t10;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <p className="mt-2 px-1 text-caption text-foreground-muted">We'll also text you a link so you can finish later.</p>;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t11) {
    t13 = <div className={t4}>{t11}{t12}</div>;
    $[19] = t11;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  return t13;
};
export const BookingComplete = t0 => {
  const $ = _c(64);
  const {
    className
  } = t0;
  const {
    selectedServices,
    bookingLocation: selectedLocation,
    isVirtualServiceBooking,
    selectedDateTime,
    createdAppointmentId,
    patientType,
    startOver,
    bookingFeeMode
  } = useSelfScheduleContext();
  const {
    selectedAddons
  } = useSelfScheduleStore();
  const pageTimezone = useStoreTimezone();
  const pathname = usePathname();
  let t1;
  if ($[0] !== pathname) {
    t1 = pathname?.includes("/embed/") ?? false;
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isEmbed = t1;
  const {
    claimedPatient: patient,
    authMode
  } = usePatientClaim();
  const {
    allFutureAppointments
  } = usePatientVerification();
  let t2;
  if ($[2] !== allFutureAppointments || $[3] !== createdAppointmentId) {
    let t3;
    if ($[5] !== createdAppointmentId) {
      t3 = a => a.id === createdAppointmentId;
      $[5] = createdAppointmentId;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = allFutureAppointments.find(t3);
    $[2] = allFutureAppointments;
    $[3] = createdAppointmentId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const bookedAppointment = t2;
  const managementStart = bookedAppointment?.start ?? selectedDateTime;
  let t3;
  if ($[7] !== managementStart) {
    t3 = managementStart ? isWithinAppointmentManagementGuard(managementStart) : false;
    $[7] = managementStart;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const isWithin24h = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      ...tenantQueryOptions.public(),
      select: _temp3
    };
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    data: redirectUrl
  } = useQuery(t4);
  let t5;
  let t6;
  if ($[10] !== isEmbed || $[11] !== redirectUrl) {
    t5 = () => {
      if (redirectUrl && !isEmbed) {
        window.location.href = redirectUrl;
      }
    };
    t6 = [redirectUrl, isEmbed];
    $[10] = isEmbed;
    $[11] = redirectUrl;
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  useEffect(t5, t6);
  const hasTrackedBookingRef = useRef(false);
  let t7;
  let t8;
  if ($[14] !== bookingFeeMode || $[15] !== createdAppointmentId || $[16] !== patientType || $[17] !== selectedAddons || $[18] !== selectedLocation || $[19] !== selectedServices) {
    t7 = () => {
      if (createdAppointmentId && !hasTrackedBookingRef.current && selectedServices?.length && selectedLocation) {
        const bookingFeeCents = computeTotalBookingFeeCents(flattenSelectedServices(selectedServices ?? [], selectedAddons), bookingFeeMode);
        const bookingFeeAmountDollars = bookingFeeCents / 100;
        trackBookingComplete(createdAppointmentId, patientType, formatList(selectedServices?.map(_temp4) ?? []), selectedLocation.name, bookingFeeAmountDollars);
        recordConversion("booking_completed", {
          eventId: createdAppointmentId,
          value: bookingFeeAmountDollars,
          currency: "USD"
        });
        hasTrackedBookingRef.current = true;
      }
    };
    t8 = [createdAppointmentId, patientType, selectedServices, selectedAddons, selectedLocation, bookingFeeMode];
    $[14] = bookingFeeMode;
    $[15] = createdAppointmentId;
    $[16] = patientType;
    $[17] = selectedAddons;
    $[18] = selectedLocation;
    $[19] = selectedServices;
    $[20] = t7;
    $[21] = t8;
  } else {
    t7 = $[20];
    t8 = $[21];
  }
  useEffect(t7, t8);
  const hasCompleteData = selectedServices?.length && selectedLocation && selectedDateTime && patient;
  if (!hasCompleteData) {
    let t9;
    if ($[22] !== className) {
      t9 = cx("motion-ease-spring-bouncy", "motion-scale-in-105 motion-opacity-in-85", className);
      $[22] = className;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    let t10;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <div className="flex h-64 items-center justify-center"><div className="text-center"><p className="text-muted-foreground">Loading booking details...</p></div></div>;
      $[24] = t10;
    } else {
      t10 = $[24];
    }
    let t11;
    if ($[25] !== t9) {
      t11 = <div className={t9}>{t10}</div>;
      $[25] = t9;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    return t11;
  }
  let t9;
  if ($[27] !== selectedServices) {
    t9 = formatList(selectedServices?.map(_temp5) ?? []);
    $[27] = selectedServices;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const serviceNames = t9;
  let t10;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = ["You'll receive a confirmation text shortly", "We'll send you a reminder 24 hours before your appointment", "You can modify or cancel up to 24 hours in advance"];
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const nextSteps = t10;
  let t11;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <CompletionHeader title="You're all set"><p>Your appointment is confirmed. A receipt has been sent to your phone.</p></CompletionHeader>;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      animationDelay: "300ms"
    };
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== isVirtualServiceBooking || $[33] !== pageTimezone || $[34] !== selectedDateTime || $[35] !== selectedLocation) {
    t13 = selectedDateTime && <SummaryRow icon={FaCalendarAlt} eyebrow="Date & time" title={formatDateWithDay(selectedDateTime)} detail={isVirtualServiceBooking && selectedLocation ? formatVisitTime(selectedDateTime, selectedLocation.timezone, pageTimezone) : formatTime(selectedDateTime)} />;
    $[32] = isVirtualServiceBooking;
    $[33] = pageTimezone;
    $[34] = selectedDateTime;
    $[35] = selectedLocation;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== isVirtualServiceBooking || $[38] !== selectedLocation) {
    t14 = selectedLocation && <SummaryRow icon={FaMapMarkerAlt} eyebrow="Location" title={isVirtualServiceBooking ? "Telehealth visit" : selectedLocation.name} detail={isVirtualServiceBooking ? VIRTUAL_VISIT_DETAIL : selectedLocation.isVirtual ? "Telehealth appointment" : formatAddress(selectedLocation.addressLineOne, selectedLocation.city, selectedLocation.state, selectedLocation.zipCode, selectedLocation.addressLineTwo, "text-caption text-foreground-muted")} />;
    $[37] = isVirtualServiceBooking;
    $[38] = selectedLocation;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== selectedServices || $[41] !== serviceNames) {
    t15 = selectedServices && selectedServices.length > 0 && <SummaryRow icon={FaUserNurse} eyebrow={selectedServices.length === 1 ? "Service" : "Services"} title={serviceNames} />;
    $[40] = selectedServices;
    $[41] = serviceNames;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== t13 || $[44] !== t14 || $[45] !== t15) {
    t16 = <div className="motion-preset-slide-right-sm overflow-hidden rounded-xl border border-border bg-background" style={t12}><div className="divide-y divide-border px-4">{t13}{t14}{t15}</div></div>;
    $[43] = t13;
    $[44] = t14;
    $[45] = t15;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <AppointmentForms />;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== authMode || $[49] !== patient || $[50] !== redirectUrl) {
    t18 = !redirectUrl && <PatientPortalSignUpPrompt className="motion-preset-slide-right-sm mt-4" authMode={authMode} claimedPatient={patient} />;
    $[48] = authMode;
    $[49] = patient;
    $[50] = redirectUrl;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <ShopUpsellPrompt className="motion-preset-slide-right-sm mt-4" />;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  let t21;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      animationDelay: "1000ms"
    };
    t21 = <h3 className="mb-3 px-1 text-caption font-semibold uppercase tracking-wider text-foreground-secondary">What's next</h3>;
    $[53] = t20;
    $[54] = t21;
  } else {
    t20 = $[53];
    t21 = $[54];
  }
  let t22;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="motion-preset-slide-right-sm mt-6" style={t20}>{t21}<ul className="space-y-3">{nextSteps.map(_temp6)}</ul></div>;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== isWithin24h || $[57] !== startOver) {
    t23 = !isWithin24h && <div className="motion-preset-slide-right-sm mt-8 flex justify-center" style={{
      animationDelay: "1100ms"
    }}><Button variant="ghost" size="sm" className="text-foreground-secondary underline-offset-4 hover:underline" onClick={() => {
        startOver();
      }}><span>Need to cancel or reschedule?</span></Button></div>;
    $[56] = isWithin24h;
    $[57] = startOver;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== className || $[60] !== t16 || $[61] !== t18 || $[62] !== t23) {
    t24 = <Completion testId="step-booking-complete" className={className}>{t11}{t16}{t17}{t18}{t19}{t22}{t23}</Completion>;
    $[59] = className;
    $[60] = t16;
    $[61] = t18;
    $[62] = t23;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  return t24;
};
export default BookingComplete;
function _temp(form) {
  return form.name;
}
function _temp2(form_0) {
  return form_0.name;
}
function _temp3(data) {
  return data?.selfSchedulingRedirectUrl;
}
function _temp4(service) {
  return service.name;
}
function _temp5(service_0) {
  return service_0.name;
}
function _temp6(step) {
  return <li key={step} className="flex items-start gap-3"><span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-status-success-bg text-status-success-text"><FaCheck className="h-3 w-3" /></span><span className="text-body text-foreground-secondary">{step}</span></li>;
}